// 设置段（Phase 1 #6）—— 危险操作区 + Phase 3 占位项。
//
// 设计原则：
// - 删除 API 是不可逆操作，必须二次确认 + 输入服务名才能 enable 删除按钮，
//   防止误点。
// - "详细日志捕获" / "单 endpoint 保留期延长" 是 Phase 3 落地的，但提前露出
//   占位，让服务商知道这块功能在路上、由 RapidAPI 30 天默认覆盖。

import React, { useState } from 'react';
import { XPrim, XIcons } from '../../primitives.jsx';
import { XShell } from '../../shell.jsx';
import { XApi } from '../../services.js';

export const SettingsSection = ({ api, onApiRefreshed }) => {
  const { navigate } = XShell.useRoute();
  const { toast } = XPrim.useToast();
  const [confirmName, setConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);
  // Phase 3: detailed logs toggle 通电。enableDetailedLogs 默认 true（对齐
  // RapidAPI）。本地 state 用于乐观更新，PATCH 完成后重新拉服务同步真实状态。
  const [detailedLogs, setDetailedLogs] = useState(
    api?.enableDetailedLogs !== false,
  );
  const [savingLogs, setSavingLogs] = useState(false);

  const apiName = api?.name || '';
  const canDelete = confirmName.trim() === apiName && !deleting;

  const onDelete = async () => {
    if (!canDelete) return;
    setDeleting(true);
    try {
      await XApi.apiSvc.delete(api.id);
      toast(`Deleted "${apiName}"`);
      navigate('workspace');
    } catch (e) {
      toast(e?.message || 'Failed to delete API');
      setDeleting(false);
    }
  };

  const onToggleDetailedLogs = async (next) => {
    setDetailedLogs(next); // 乐观更新
    setSavingLogs(true);
    try {
      await XApi.apiSvc.updateBasic(api.id, { enableDetailedLogs: next });
      toast({
        title: next
          ? '已开启详细日志捕获（30 天保留）'
          : '已关闭详细日志捕获（失败请求仍会捕获）',
        type: 'success',
      });
      const fresh = await XApi.apiSvc.get(api.id);
      if (fresh) {
        setDetailedLogs(fresh.enableDetailedLogs !== false);
        onApiRefreshed?.(fresh);
      }
    } catch (e) {
      toast({ title: e?.message || '保存失败', type: 'error' });
      setDetailedLogs(!next); // 回滚
    } finally {
      setSavingLogs(false);
    }
  };

  return (
    <div style={{ padding: '32px 32px 64px', maxWidth: 720 }}>
      <h2
        style={{
          margin: 0,
          fontSize: 16,
          fontWeight: 500,
          color: 'var(--fg-0)',
          letterSpacing: '-0.01em',
        }}
      >
        设置
      </h2>
      <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 4, marginBottom: 24 }}>
        日志策略 · 危险操作
      </div>

      {/* Phase 3 通电 —— 详细日志捕获开关 */}
      <SectionCard
        title="详细日志捕获"
        desc="对齐 RapidAPI 行业标准：默认 30 天完整请求 / 响应体捕获（截断到 4KB 每字段，30 天硬删）。关闭后只采集失败请求（debug 不受影响）。"
      >
        <ToggleRow
          label="捕获请求 / 响应详情"
          hint="包含请求头、请求体、响应头、响应体；IP 与 country 始终记录，不受此开关影响。失败请求（status≥400）始终捕获。"
          checked={detailedLogs}
          disabled={savingLogs}
          onToggle={() => onToggleDetailedLogs(!detailedLogs)}
        />
      </SectionCard>

      {/* Phase 3 占位 —— 单 endpoint 保留期延长 */}
      <SectionCard
        title="日志保留期"
        badge="Coming in Phase 3"
        desc="默认 30 天对齐 RapidAPI。如需为某些 endpoint 调长（例如长尾 debug），可在 endpoint 级别单独设置 90 天，存储费由用户承担。"
      >
        <RetentionRow current="30 天" placeholder="按 endpoint 设置 30 / 90" />
      </SectionCard>

      {/* 危险操作 */}
      <SectionCard danger title="删除 API" desc={`删除后，"${apiName}" 的所有版本、endpoints、配置都将不可恢复。已发生的调用记录与收益结算保留（合规需要），但你将无法再编辑或发布新版本。`}>
        <div style={{ marginTop: 8 }}>
          <label
            style={{
              display: 'block',
              fontSize: 12,
              color: 'var(--fg-2)',
              marginBottom: 6,
            }}
          >
            为确认，请在下方输入服务名{' '}
            <span className="mono" style={{ color: 'var(--fg-0)' }}>
              {apiName}
            </span>
          </label>
          <input
            type="text"
            className="input"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={apiName}
            disabled={deleting}
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid var(--bd-1)',
              background: 'var(--bg-0)',
              color: 'var(--fg-0)',
              fontSize: 13,
            }}
          />
          <button
            type="button"
            onClick={onDelete}
            disabled={!canDelete}
            style={{
              marginTop: 14,
              padding: '8px 16px',
              background: canDelete ? '#c0392b' : 'var(--bg-1)',
              color: canDelete ? '#fff' : 'var(--fg-3)',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 500,
              cursor: canDelete ? 'pointer' : 'not-allowed',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {XIcons.IconTrash && <XIcons.IconTrash size={13} />}
            <span>{deleting ? '删除中…' : `永久删除 ${apiName}`}</span>
          </button>
        </div>
      </SectionCard>
    </div>
  );
};

const SectionCard = ({ title, desc, badge, danger, children }) => (
  <section
    style={{
      border: `1px solid ${danger ? 'rgba(192,57,43,0.4)' : 'var(--bd-1)'}`,
      borderRadius: 10,
      padding: 20,
      marginBottom: 16,
      background: 'var(--bg-0)',
    }}
  >
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 8,
      }}
    >
      <h2
        style={{
          margin: 0,
          fontSize: 14,
          fontWeight: 500,
          color: danger ? '#c0392b' : 'var(--fg-0)',
        }}
      >
        {title}
      </h2>
      {badge && (
        <span
          style={{
            padding: '2px 8px',
            fontSize: 10,
            background: 'var(--bg-1)',
            border: '1px solid var(--bd-1)',
            borderRadius: 999,
            color: 'var(--fg-3)',
          }}
        >
          {badge}
        </span>
      )}
    </header>
    {desc && (
      <p
        style={{
          margin: 0,
          fontSize: 12,
          color: 'var(--fg-2)',
          lineHeight: 1.6,
        }}
      >
        {desc}
      </p>
    )}
    {children}
  </section>
);

const ToggleRow = ({ label, hint, checked, disabled, onToggle }) => (
  <div
    style={{
      marginTop: 12,
      padding: '10px 12px',
      background: 'var(--bg-1)',
      borderRadius: 6,
      opacity: disabled ? 0.6 : 1,
    }}
  >
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        cursor: disabled || !onToggle ? 'not-allowed' : 'pointer',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onToggle && onToggle(e.target.checked)}
        style={{ marginTop: 2 }}
      />
      <div>
        <div style={{ fontSize: 13, color: 'var(--fg-0)' }}>{label}</div>
        {hint && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--fg-3)',
              marginTop: 2,
              lineHeight: 1.5,
            }}
          >
            {hint}
          </div>
        )}
      </div>
    </label>
  </div>
);

const RetentionRow = ({ current, placeholder }) => (
  <div
    style={{
      marginTop: 12,
      padding: '10px 12px',
      background: 'var(--bg-1)',
      borderRadius: 6,
      opacity: 0.6,
      // E-fix: pointerEvents off so the "30 天" value can't be selected as
      // text or focused — visually + interactively communicates it's a
      // read-only preview of a not-yet-shipped feature, not an editable
      // setting. Paired with the "Coming in Phase 3" badge on the parent
      // card.
      pointerEvents: 'none',
      userSelect: 'none',
    }}
  >
    <div style={{ fontSize: 12, color: 'var(--fg-2)' }}>
      当前：<span className="mono" style={{ color: 'var(--fg-0)' }}>{current}</span>
      <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--fg-4)' }}>
        （暂不可调）
      </span>
    </div>
    <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 4 }}>
      {placeholder}
    </div>
  </div>
);
